/**
 * Closure-trail read service — assembles the `harmony.incidents.closure-trail`
 * contract (02-04) for one source record.
 *
 * Pure service layer — no React imports. Reads `Closure Links` where
 * `SourceRef == <ref>` (the authoritative many-to-many graph, D4), then resolves
 * each link's target Title + status against its register where cheap, otherwise
 * returns refs only. The output shape matches the 02-04 JSON literally — the
 * same shape the Portal already renders.
 *
 * Field names come ONLY from `config/registers.ts` (CLOSURE_LINK_FIELDS /
 * CARE_PLAN_REVISION_FIELDS / REGISTERS field maps). All keys are composed
 * `<PREFIX>-<Id>` text (ISS-008); the calc `Reference` columns are never read.
 */

import type { Client } from '@microsoft/microsoft-graph-client';
import { listItems } from './graphClient';
import {
  CLOSURE_CONFIG,
  CLOSURE_LINK_FIELDS,
  CARE_PLAN_REVISION_FIELDS,
  REGISTERS,
  TARGET_TYPE_TO_REGISTER_KEY,
} from '../config/registers';
import type {
  ClosureTrail,
  ClosureLink,
  ClosureStatus,
  ClosureRelationship,
  ClosureTargetType,
  LinkStatus,
} from '../types/qms';

type RawItem = Record<string, unknown> & { id: string };

function str(item: RawItem, field: string): string | null {
  const v = item[field];
  if (v === undefined || v === null || v === '') return null;
  return typeof v === 'string' ? v : String(v);
}

function escapeFilterLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

/** Compose a record's reference from its prefix + SP item id (ISS-008). */
function composeRef(prefix: string, id: number): string {
  return `${prefix}-${id}`;
}

/**
 * Resolved list ids the closure service needs. Keyed by the same `LIST_NAMES`
 * keys `useGraphClient` resolves (never hard-coded ids). Target-register ids are
 * optional — when absent (unprovisioned), targets degrade to refs-only.
 */
export interface ClosureListIds {
  /** `Closure Links` list id (required to read the trail). */
  closureLinks: string;
  /** `Continuous Improvement` list id (for `ContinuousImprovement` targets). */
  continuousImprovement?: string;
  /** `Risk Register` list id (for `Risk` targets). */
  risk?: string;
  /** `Care Plan Revisions` list id (for `CarePlanRevision` targets). */
  carePlanRevisions?: string;
}

/**
 * Read the closure trail for a source record.
 *
 * @param client - Graph client (delegated; from useGraphClient)
 * @param siteId - resolved Q&S site id (from useGraphClient)
 * @param listIds - resolved closure + target list ids (from useGraphClient.listIds)
 * @param sourceRef - the source's composed reference, e.g. `INC-0042` (ISS-008)
 */
export async function getTrail(
  client: Client,
  siteId: string,
  listIds: ClosureListIds,
  sourceRef: string,
): Promise<ClosureTrail> {
  const empty: ClosureTrail = {
    incident: sourceRef,
    closureStatus: null,
    readyToClose: false,
    consequences: [],
  };

  // Graceful handling: no Closure Links list provisioned yet, or no source ref.
  if (!listIds.closureLinks || !sourceRef) return empty;

  const links = await listItems<RawItem>(client, siteId, listIds.closureLinks, {
    filter: `fields/${CLOSURE_LINK_FIELDS.sourceRef} eq '${escapeFilterLiteral(sourceRef)}'`,
    orderby: 'fields/Created asc',
  });

  const consequences: ClosureLink[] = [];
  for (const raw of links.items) {
    const targetType = (str(raw, CLOSURE_LINK_FIELDS.targetType) ?? '') as ClosureTargetType;
    const targetRef = str(raw, CLOSURE_LINK_FIELDS.targetRef) ?? '';

    const resolved = await resolveTarget(client, siteId, listIds, targetType, targetRef);

    consequences.push({
      // The link record's own composed reference (ISS-008): LNK-<Id>.
      linkRef: composeRef(CLOSURE_CONFIG.closureLinks.prefix, Number(raw.id)),
      relationship: (str(raw, CLOSURE_LINK_FIELDS.relationship) ?? '') as ClosureRelationship,
      targetType,
      targetRef,
      targetTitle: resolved.title,
      targetStatus: resolved.status,
      linkStatus: (str(raw, CLOSURE_LINK_FIELDS.linkStatus) ?? 'Open') as LinkStatus,
    });
  }

  return {
    incident: sourceRef,
    closureStatus: null,
    readyToClose: computeReadyToClose(consequences),
    consequences,
  };
}

/**
 * 02-03 close-readiness predicate:
 * `(≥1 non-cancelled link) AND (all non-cancelled links Complete)`.
 */
export function computeReadyToClose(consequences: ClosureLink[]): boolean {
  const active = consequences.filter((c) => c.linkStatus !== 'Cancelled');
  if (active.length === 0) return false;
  return active.every((c) => c.linkStatus === 'Complete');
}

/**
 * Resolve a target's Title + status by reading its register where the list id is
 * available; otherwise return nulls (refs-only). Resolution table per 02-04:
 * - ContinuousImprovement → `Continuous Improvement`, status `Status`
 * - Risk                  → `Risk Register`, status `ClosureStatus`
 * - CarePlanRevision      → `Care Plan Revisions`, status `ApprovalStatus`
 */
async function resolveTarget(
  client: Client,
  siteId: string,
  listIds: ClosureListIds,
  targetType: ClosureTargetType,
  targetRef: string,
): Promise<{ title: string | null; status: string | null }> {
  const none = { title: null, status: null };
  if (!targetRef) return none;

  if (targetType === 'CarePlanRevision') {
    if (!listIds.carePlanRevisions) return none;
    const item = await findByComposedRef(
      client,
      siteId,
      listIds.carePlanRevisions,
      CLOSURE_CONFIG.carePlanRevisions.prefix,
      targetRef,
    );
    if (!item) return none;
    return {
      title: str(item, CARE_PLAN_REVISION_FIELDS.title),
      status: str(item, CARE_PLAN_REVISION_FIELDS.approvalStatus),
    };
  }

  const registerKey = TARGET_TYPE_TO_REGISTER_KEY[targetType];
  if (!registerKey) return none;

  const listId =
    registerKey === 'ci'
      ? listIds.continuousImprovement
      : registerKey === 'risk'
        ? listIds.risk
        : undefined;
  if (!listId) return none;

  const cfg = REGISTERS[registerKey];
  const item = await findByComposedRef(client, siteId, listId, cfg.prefix, targetRef);
  if (!item) return none;

  // CI reads `Status`; Risk reads `ClosureStatus` (02-04 resolution table).
  const statusField = registerKey === 'risk' ? cfg.fields.closureStatus : cfg.fields.status;
  return {
    title: str(item, cfg.fields.title),
    status: str(item, statusField),
  };
}

/**
 * Find the register item whose COMPOSED `<PREFIX>-<Id>` equals `targetRef`
 * (02-04: "the register item whose composed `<PREFIX>-<Id>` equals `TargetRef`").
 * We parse the id out of the ref and read that item directly by id — never the
 * calc `Reference` column (ISS-008).
 */
async function findByComposedRef(
  client: Client,
  siteId: string,
  listId: string,
  prefix: string,
  targetRef: string,
): Promise<RawItem | null> {
  const expectedPrefix = `${prefix}-`;
  if (!targetRef.startsWith(expectedPrefix)) return null;
  const idPart = targetRef.slice(expectedPrefix.length);
  const id = Number(idPart);
  if (!Number.isFinite(id) || id <= 0) return null;

  // Read by SP item id directly (the immutable stable key, ISS-008).
  const result = await listItems<RawItem>(client, siteId, listId, {
    filter: `fields/id eq ${id}`,
  });
  return result.items[0] ?? null;
}

/** Re-export for hooks that need to type their resolved-list-id object. */
export type { ClosureStatus };
